import { useCallback, useEffect, useState } from "react";
import { get as commsGet } from "../../../../service/comms/commsApi";
import {
  getCoachSupportMetrics,
  classifyOversightError,
} from "../../../../service/comms/oversightAnalyticsService";

/**
 * Support-desk ticket metrics for one coach
 * (`GET /oversight/analytics/coaches/{id}/support`).
 *
 * Lifted out of `SupportDeskCard` for two reasons, both about the tab shell:
 *
 * 1. **The card lives on the default tab, and tabs unmount on switch.** Fetching
 *    inside it meant a fresh request every time the user came back to Overview.
 * 2. **It removes a waterfall.** `CoachDetail` renders no tab at all while the
 *    parent analytics call is in flight, so a card-owned fetch could not start
 *    until that round-trip had already finished. Owned by the shell, it goes
 *    out in parallel with the analytics and roster calls instead.
 *
 * Same shape and error contract as `useCoachClients`, deliberately — the two sit
 * side by side in the shell and should read identically.
 *
 * @param {string|null|undefined} coachId - Resolved coach id.
 * @param {{date_from: string, date_to: string}} range - Selected date range.
 * @param {'reports'|'company'} [scope] - Report-chain scope, carried over from
 *   the coaches list so the detail page resolves the same roster the list did.
 * @returns {{ totals: Object|null, loading: boolean,
 *   errorKind: 'forbidden'|'gateway'|'other'|null, retry: () => void }}
 */
export function useCoachSupportMetrics(coachId, range, scope) {
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  /** @type {[('forbidden'|'gateway'|'other'|null), Function]} */
  const [errorKind, setErrorKind] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);

  // Active-cleanup fetch, so a stale in-flight response can't clobber a newer
  // one when the range changes twice in quick succession.
  useEffect(() => {
    if (!coachId) {
      setLoading(false);
      return undefined;
    }
    let active = true;
    setLoading(true);
    setErrorKind(null);
    getCoachSupportMetrics(commsGet, coachId, { ...range, scope })
      .then((data) => {
        if (!active) return;
        setTotals(data?.totals ?? null);
      })
      .catch((err) => {
        if (!active) return;
        setErrorKind(classifyOversightError(err));
        setTotals(null);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [coachId, range, scope, reloadToken]);

  const retry = useCallback(() => setReloadToken((t) => t + 1), []);

  return { totals, loading, errorKind, retry };
}

export default useCoachSupportMetrics;
